import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface FinalizeWhatsAppConnectionInput {
  code: string;
  wabaId: string;
}

export interface FinalizeWhatsAppConnectionResponse {
  accounts: Array<{ id: string; phoneNumberId: string; wabaId: string }>;
}

interface UseFinalizeWhatsAppConnectionOptions {
  onSuccess?: (data: FinalizeWhatsAppConnectionResponse) => void;
  onError?: (error: Error) => void;
}

export const useFinalizeWhatsAppConnection = (
  options?: UseFinalizeWhatsAppConnectionOptions
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: FinalizeWhatsAppConnectionInput) =>
      apiClient.post<FinalizeWhatsAppConnectionResponse>(
        'integrations/whatsapp/finalize',
        input
      ),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.integrations.whatsapp(),
      });
      toast.success('WhatsApp connected');
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      // A blocked number is still saved, so the accounts list has changed.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.integrations.whatsapp(),
      });
      // Long enough to read: a number Meta blocks comes back with what is
      // wrong and how to fix it, not a one-word failure.
      toast.error(error.message || 'Failed to finalize WhatsApp connection', {
        duration: 15_000,
      });
      options?.onError?.(error);
    },
  });

  return {
    finalize: mutation.mutate,
    finalizeAsync: mutation.mutateAsync,
    isFinalizing: mutation.isPending,
  };
};
